import { Component } from 'preact';
import { TEXT_PRIMARY, TEXT_MUTED, ACCENT_GOLD, OPAQUE_TEXTURED_PANEL_STYLE } from '../../lib/medievalTheme';
import { KeystoneIcon, CoinIcon, IconChevronDown } from './icons';
import { UI } from '../../utils/arena/uiSounds';
import { t } from '../../localization/t';

const CONTROL_RADIUS = 'var(--radius-md)';

/**
 * Accept-and-pay for a priced event entry. The price rides on the button,
 * and the chevron opens the pick-your-currency menu. Splitting the two
 * would let a player accept before choosing what it comes out of.
 * Keystones lead when the player holds enough; the menu opens upward
 * because every host of this control sits at the foot of a clipped plate.
 *
 * Shared by the DM draft invite and the match-found overlay, so the two
 * ways into a paid event are the same control.
 */
export default class AcceptPayControl extends Component {
  state = { open: false };

  pick = (method) => {
    this.setState({ open: false });
    this.props.onAccept?.(method);
  };

  render() {
    const { cost = 0, ksCost = 1, coins = 0, keystones = 0 } = this.props;
    const { open } = this.state;
    const canKeystone = keystones >= ksCost;
    const canCoins = coins >= cost;
    const cantAfford = !canKeystone && !canCoins;
    const ksLabel = ksCost > 1 ? t('{n} Keystones', { n: ksCost }) : t('1 Keystone');

    let acceptTitle;
    if (cantAfford) acceptTitle = t('Not enough. This costs {cost} gold or {keystones}.', { cost, keystones: ksLabel });
    else if (canKeystone) acceptTitle = t('Accept and spend {keystones}', { keystones: ksLabel });
    else acceptTitle = t('Accept and pay {cost} gold', { cost });

    return (
      <div className="relative flex items-stretch" style={{ opacity: cantAfford ? 0.5 : 1 }}>
        <button
          type="button"
          className="vh-invite-btn is-primary inline-flex items-center gap-1.5 whitespace-nowrap"
          style={{ borderRadius: `${CONTROL_RADIUS} 0 0 ${CONTROL_RADIUS}` }}
          data-sound={UI.CONFIRM}
          disabled={cantAfford}
          title={acceptTitle}
          onClick={() => this.pick(canKeystone ? 'keystone' : 'coins')}
        >
          <span>{t('Accept')}</span>
          <span style={{ opacity: 0.75 }}>&middot;</span>
          <span className="inline-flex items-center gap-0.5 tabular-nums">
            {canKeystone ? ksCost : cost}
            {canKeystone ? <KeystoneIcon size={11} /> : <CoinIcon size={11} />}
          </span>
        </button>
        <button
          type="button"
          className="vh-invite-btn is-primary px-1.5 flex items-center"
          style={{ borderRadius: `0 ${CONTROL_RADIUS} ${CONTROL_RADIUS} 0`, borderLeft: '1px solid rgba(90,65,20,0.45)' }}
          data-sound={UI.CLICK_SOFT}
          disabled={cantAfford}
          title={t('Choose how to pay')}
          aria-label={t('Choose how to pay')}
          aria-expanded={open}
          onClick={() => this.setState((s) => ({ open: !s.open }))}
        >
          <IconChevronDown style={{ width: '0.75cqw', height: '0.75cqw', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease' }} />
        </button>
        {open ? (
          <div
            className="absolute left-0 z-10 flex flex-col"
            style={{
              bottom: 'calc(100% + 0.25cqw)',
              padding: CONTROL_RADIUS,
              ...OPAQUE_TEXTURED_PANEL_STYLE,
              borderRadius: 'var(--radius-lg)',
              boxShadow: '0 12px 40px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(180,140,60,0.2)',
            }}
          >
            {[
              { method: 'keystone', icon: <KeystoneIcon size={12} />, label: ksLabel, have: keystones, ok: canKeystone },
              { method: 'coins', icon: <CoinIcon size={12} />, label: t('{n} Gold', { n: cost.toLocaleString() }), have: coins, ok: canCoins },
            ].map((opt) => (
              <button
                key={opt.method}
                type="button"
                className="vh-invite-btn is-ghost flex items-center gap-2 pl-2 pr-2.5 py-1.5 whitespace-nowrap"
                style={{ color: opt.ok ? TEXT_PRIMARY : TEXT_MUTED, borderRadius: CONTROL_RADIUS, opacity: opt.ok ? 1 : 0.5 }}
                disabled={!opt.ok}
                data-sound={UI.CONFIRM}
                onClick={() => this.pick(opt.method)}
              >
                <span className="flex items-center justify-center shrink-0" style={{ width: '1cqw', color: opt.ok ? ACCENT_GOLD : TEXT_MUTED }}>{opt.icon}</span>
                <span className="text-[0.6875cqw] font-semibold">{opt.label}</span>
                <span className="ml-auto pl-4 text-[0.5625cqw] tabular-nums" style={{ color: TEXT_MUTED }}>{t('{n} owned', { n: opt.have.toLocaleString() })}</span>
              </button>
            ))}
          </div>
        ) : null}
      </div>
    );
  }
}
